import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import useAuthStore from '../store/authStore';

interface AnswerDetail {
  questionNumber: number;
  questionId: number | null;
  source: string;
  questionText: string;
  optionA: string; optionB: string; optionC: string; optionD: string;
  correctOption: string;
  selectedOption: string;
  correct: boolean;
  explanation: string;
}

interface MathResult {
  id: number;
  grade: number;
  score: number;
  totalQuestions: number;
  percentage: number;
  timeTaken: number;
  attemptedAt: string;
  answersJson: string;
  topic: { id: number; topicName: string };
}

interface ReadingResult {
  id: number;
  grade: number;
  score: number;
  totalQuestions: number;
  percentage: number;
  timeTaken: number;
  attemptedAt: string;
  answersJson: string;
  story: { id: number; title: string };
}

interface SubjectResult {
  id: number;
  grade: number;
  score: number;
  totalQuestions: number;
  percentage: number;
  timeTaken: number;
  attemptedAt: string;
  answersJson: string;
  subjectId: number | null;
  subjectName: string;
  topicId: number | null;
  topicName: string;
}

const QuizHistory: React.FC<{ kidId?: number | string }> = ({ kidId }) => {
  const { userId } = useAuthStore();
  const effectiveKidId = kidId ?? userId;
  const navigate = useNavigate();
  const [tab, setTab] = useState<'math' | 'reading' | 'subjects'>('math');
  const [mathResults, setMathResults] = useState<MathResult[]>([]);
  const [readingResults, setReadingResults] = useState<ReadingResult[]>([]);
  const [subjectResults, setSubjectResults] = useState<SubjectResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const parseAnswers = (json: string): AnswerDetail[] => {
    try { return JSON.parse(json) || []; } catch { return []; }
  };

  useEffect(() => {
    fetchResults();
  }, []);

  const fetchResults = async () => {
    setLoading(true);
    try {
      const [mathRes, readingRes, subjRes] = await Promise.all([
        api.get(`/api/kids/${effectiveKidId}/math/results`),
        api.get(`/api/kids/${effectiveKidId}/reading/results`),
        api.get(`/api/kids/${effectiveKidId}/subjects/results`),
      ]);
      setMathResults(mathRes.data);
      setReadingResults(readingRes.data);
      setSubjectResults(subjRes.data);
    } catch {}
    finally { setLoading(false); }
  };

  const getEmoji = (pct: number) => pct >= 80 ? '🌟' : pct >= 60 ? '😊' : '💪';
  const getColor = (pct: number) => pct >= 80 ? 'text-green-600' : pct >= 60 ? 'text-yellow-500' : 'text-red-400';
  const getBorder = (pct: number) => pct >= 80 ? 'border-green-400' : pct >= 60 ? 'border-yellow-400' : 'border-red-400';

  const formatDate = (dt: string) =>
    new Date(dt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const formatTime = (secs: number) =>
    secs ? `${Math.floor(secs / 60)}m ${secs % 60}s` : '-';

  const avgPct = (results: any[]) =>
    results.length ? Math.round(results.reduce((s, r) => s + r.percentage, 0) / results.length) : 0;

  return (
    <div>
      <main className="space-y-4">
        <h1 className="text-xl font-display font-extrabold text-gray-800">📊 My Quiz History</h1>

        {/* Summary cards */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-orange-50 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-orange-500">{mathResults.length}</p>
            <p className="text-sm text-gray-500">Math</p>
            {mathResults.length > 0 && (
              <p className="text-sm font-semibold text-orange-400 mt-1">Avg: {avgPct(mathResults)}%</p>
            )}
          </div>
          <div className="bg-purple-50 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-purple-600">{readingResults.length}</p>
            <p className="text-sm text-gray-500">Reading</p>
            {readingResults.length > 0 && (
              <p className="text-sm font-semibold text-purple-400 mt-1">Avg: {avgPct(readingResults)}%</p>
            )}
          </div>
          <div className="bg-pink-50 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-pink-600">{subjectResults.length}</p>
            <p className="text-sm text-gray-500">Subjects</p>
            {subjectResults.length > 0 && (
              <p className="text-sm font-semibold text-pink-400 mt-1">Avg: {avgPct(subjectResults)}%</p>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex bg-white rounded-xl shadow-sm overflow-hidden">
          <button
            onClick={() => setTab('math')}
            className={`flex-1 py-3 text-sm font-semibold transition ${
              tab === 'math' ? 'bg-orange-500 text-white' : 'text-gray-500 hover:bg-gray-50'
            }`}
          >
            🧮 Math ({mathResults.length})
          </button>
          <button
            onClick={() => setTab('reading')}
            className={`flex-1 py-3 text-sm font-semibold transition ${
              tab === 'reading' ? 'bg-purple-600 text-white' : 'text-gray-500 hover:bg-gray-50'
            }`}
          >
            📖 Reading ({readingResults.length})
          </button>
          <button
            onClick={() => setTab('subjects')}
            className={`flex-1 py-3 text-sm font-semibold transition ${
              tab === 'subjects' ? 'bg-pink-600 text-white' : 'text-gray-500 hover:bg-gray-50'
            }`}
          >
            🎓 Subjects ({subjectResults.length})
          </button>
        </div>

        {/* Results list */}
        {loading ? (
          <p className="text-center text-gray-400 py-8">Loading...</p>
        ) : tab === 'math' ? (
          mathResults.length === 0 ? (
            <div className="text-center py-10 text-gray-400">
              <div className="text-4xl mb-2">🧮</div>
              <p>No math quizzes attempted yet.</p>
              <button onClick={() => navigate('/kid/math')}
                className="mt-3 text-orange-500 text-sm underline">Take a quiz →</button>
            </div>
          ) : (
            <div className="space-y-3">
              {mathResults.map(r => (
                <div key={r.id} className={`bg-white rounded-xl shadow-sm border-l-4 ${getBorder(r.percentage)}`}>
                  <div className="p-4 cursor-pointer" onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}>
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold text-gray-800">{r.topic?.topicName || 'Math Quiz'}</p>
                        <p className="text-xs text-gray-400 mt-0.5">Grade {r.grade} · {formatDate(r.attemptedAt)}</p>
                        <p className="text-xs text-gray-400">Time: {formatTime(r.timeTaken)}</p>
                      </div>
                      <div className="text-right">
                        <p className={`text-2xl font-bold ${getColor(r.percentage)}`}>
                          {getEmoji(r.percentage)} {Math.round(r.percentage)}%
                        </p>
                        <p className="text-xs text-gray-400">{r.score}/{r.totalQuestions} correct</p>
                      </div>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5 mt-3">
                      <div className={`h-1.5 rounded-full ${r.percentage >= 80 ? 'bg-green-400' : r.percentage >= 60 ? 'bg-yellow-400' : 'bg-red-400'}`}
                        style={{ width: `${r.percentage}%` }} />
                    </div>
                    <p className="text-xs text-blue-400 mt-2 text-right">
                      {expandedId === r.id ? '▲ Hide review' : '▼ Review answers'}
                    </p>
                  </div>
                  {expandedId === r.id && (
                    <div className="border-t border-gray-100 p-4 space-y-3">
                      {parseAnswers(r.answersJson).map((a, i) => (
                        <div key={i} className={`rounded-lg p-3 text-sm ${a.correct ? 'bg-green-50' : 'bg-red-50'}`}>
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <p className="font-medium text-gray-800">{i + 1}. {a.questionText}</p>
                            <span className={`text-lg flex-shrink-0 ${a.correct ? 'text-green-500' : 'text-red-400'}`}>
                              {a.correct ? '✓' : '✗'}
                            </span>
                          </div>
                          {['A','B','C','D'].map(opt => (
                            <div key={opt} className={`px-2 py-1 rounded mb-0.5 text-xs
                              ${opt === a.correctOption ? 'bg-green-200 text-green-800 font-semibold' :
                                opt === a.selectedOption && !a.correct ? 'bg-red-200 text-red-700' : 'text-gray-600'}`}>
                              {opt}. {(a as any)[`option${opt}`]}
                            </div>
                          ))}
                          {a.explanation && <p className="text-xs text-gray-400 mt-1 italic">💡 {a.explanation}</p>}
                          {a.source === 'DB' && a.questionId && (
                            <p className="text-xs text-blue-300 mt-1">DB Question #{a.questionId}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )
        ) : tab === 'reading' ? (
          readingResults.length === 0 ? (
            <div className="text-center py-10 text-gray-400">
              <div className="text-4xl mb-2">📖</div>
              <p>No reading quizzes attempted yet.</p>
              <button onClick={() => navigate('/kid/reading')}
                className="mt-3 text-purple-500 text-sm underline">Read a story →</button>
            </div>
          ) : (
            <div className="space-y-3">
              {readingResults.map(r => (
                <div key={r.id} className={`bg-white rounded-xl shadow-sm border-l-4 ${getBorder(r.percentage)}`}>
                  <div className="p-4 cursor-pointer" onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}>
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold text-gray-800">{r.story?.title || 'Reading Quiz'}</p>
                        <p className="text-xs text-gray-400 mt-0.5">Grade {r.grade} · {formatDate(r.attemptedAt)}</p>
                        <p className="text-xs text-gray-400">Time: {formatTime(r.timeTaken)}</p>
                      </div>
                      <div className="text-right">
                        <p className={`text-2xl font-bold ${getColor(r.percentage)}`}>
                          {getEmoji(r.percentage)} {Math.round(r.percentage)}%
                        </p>
                        <p className="text-xs text-gray-400">{r.score}/{r.totalQuestions} correct</p>
                      </div>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5 mt-3">
                      <div className={`h-1.5 rounded-full ${r.percentage >= 80 ? 'bg-green-400' : r.percentage >= 60 ? 'bg-yellow-400' : 'bg-red-400'}`}
                        style={{ width: `${r.percentage}%` }} />
                    </div>
                    <p className="text-xs text-blue-400 mt-2 text-right">
                      {expandedId === r.id ? '▲ Hide review' : '▼ Review answers'}
                    </p>
                  </div>
                  {expandedId === r.id && (
                    <div className="border-t border-gray-100 p-4 space-y-3">
                      {parseAnswers(r.answersJson).map((a, i) => (
                        <div key={i} className={`rounded-lg p-3 text-sm ${a.correct ? 'bg-green-50' : 'bg-red-50'}`}>
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <p className="font-medium text-gray-800">{i + 1}. {a.questionText}</p>
                            <span className={`text-lg flex-shrink-0 ${a.correct ? 'text-green-500' : 'text-red-400'}`}>
                              {a.correct ? '✓' : '✗'}
                            </span>
                          </div>
                          {['A','B','C','D'].map(opt => (
                            <div key={opt} className={`px-2 py-1 rounded mb-0.5 text-xs
                              ${opt === a.correctOption ? 'bg-green-200 text-green-800 font-semibold' :
                                opt === a.selectedOption && !a.correct ? 'bg-red-200 text-red-700' : 'text-gray-600'}`}>
                              {opt}. {(a as any)[`option${opt}`]}
                            </div>
                          ))}
                          {a.explanation && <p className="text-xs text-gray-400 mt-1 italic">💡 {a.explanation}</p>}
                          {a.source === 'DB' && a.questionId && (
                            <p className="text-xs text-blue-300 mt-1">DB Question #{a.questionId}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )
        ) : (
          subjectResults.length === 0 ? (
            <div className="text-center py-10 text-gray-400">
              <div className="text-4xl mb-2">🎓</div>
              <p>No subject quizzes attempted yet.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {subjectResults.map(r => (
                <div key={r.id} className={`bg-white rounded-xl shadow-sm border-l-4 ${getBorder(r.percentage)}`}>
                  <div className="p-4 cursor-pointer" onClick={() => setExpandedId(expandedId === r.id ? null : r.id)}>
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold text-gray-800">{r.topicName || 'Quiz'}</p>
                        <p className="text-xs text-pink-500 font-medium">{r.subjectName}</p>
                        <p className="text-xs text-gray-400 mt-0.5">Grade {r.grade} · {formatDate(r.attemptedAt)}</p>
                        <p className="text-xs text-gray-400">Time: {formatTime(r.timeTaken)}</p>
                      </div>
                      <div className="text-right">
                        <p className={`text-2xl font-bold ${getColor(r.percentage)}`}>
                          {getEmoji(r.percentage)} {Math.round(r.percentage)}%
                        </p>
                        <p className="text-xs text-gray-400">{r.score}/{r.totalQuestions} correct</p>
                      </div>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5 mt-3">
                      <div className={`h-1.5 rounded-full ${r.percentage >= 80 ? 'bg-green-400' : r.percentage >= 60 ? 'bg-yellow-400' : 'bg-red-400'}`}
                        style={{ width: `${r.percentage}%` }} />
                    </div>
                    <p className="text-xs text-blue-400 mt-2 text-right">
                      {expandedId === r.id ? '▲ Hide review' : '▼ Review answers'}
                    </p>
                  </div>
                  {expandedId === r.id && (
                    <div className="border-t border-gray-100 p-4 space-y-3">
                      {parseAnswers(r.answersJson).map((a, i) => (
                        <div key={i} className={`rounded-lg p-3 text-sm ${a.correct ? 'bg-green-50' : 'bg-red-50'}`}>
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <p className="font-medium text-gray-800">{i + 1}. {a.questionText}</p>
                            <span className={`text-lg flex-shrink-0 ${a.correct ? 'text-green-500' : 'text-red-400'}`}>
                              {a.correct ? '✓' : '✗'}
                            </span>
                          </div>
                          {['A','B','C','D'].map(opt => (
                            <div key={opt} className={`px-2 py-1 rounded mb-0.5 text-xs
                              ${opt === a.correctOption ? 'bg-green-200 text-green-800 font-semibold' :
                                opt === a.selectedOption && !a.correct ? 'bg-red-200 text-red-700' : 'text-gray-600'}`}>
                              {opt}. {(a as any)[`option${opt}`]}
                            </div>
                          ))}
                          {a.explanation && <p className="text-xs text-gray-400 mt-1 italic">💡 {a.explanation}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )
        )}
      </main>
    </div>
  );
};

export default QuizHistory;
